import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BuildingIcon, PencilIcon, PlusIcon, SearchIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CreateClientDialog } from "@/components/CreateClientDialog";
import { EditClientDialog } from "@/components/EditClientDialog";
import { ClientStatsHoverCard } from "@/components/ClientStatsHoverCard";
import { formatDateTime } from "@/lib/format";

type Client = components["schemas"]["ClientAdminResponse"];
type ClientType = components["schemas"]["ClientType"];

// "all" is sent as no Type at all - the server reads a missing Type as both tenancy modes.
type TypeFilter = ClientType | "all";

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All types" },
  { value: "Live", label: "Live" },
  { value: "Sandbox", label: "Sandbox" },
  { value: "Demo", label: "Demo" },
];

function formatPercent(value: number | string | undefined): string {
  if (value === undefined || value === null) return "—";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return "—";
  return `${num.toFixed(2)}%`;
}

function createdTime(client: Client): number {
  return client.createdAt ? new Date(client.createdAt).getTime() : 0;
}

// A trigram is three characters; the server ignores shorter queries, so don't send them.
const MIN_SEARCH_LENGTH = 3;

const PAGE_LIMIT = 50;

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  // Not `clients.length > 0`: an empty result has loaded and mustn't bring back the full-page spinner.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editClient, setEditClient] = useState<Client | null>(null);

  const [searchInput, setSearchInput] = useState("");
  // 400ms, the shared default every other search box in this app uses.
  const debouncedSearch = useDebouncedValue(searchInput);
  const searchTerm = debouncedSearch.trim();
  const activeSearch = searchTerm.length >= MIN_SEARCH_LENGTH ? searchTerm : undefined;

  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  // Off by default: clones are match-testing copies and would crowd out their sources.
  const [showClones, setShowClones] = useState(false);

  // Forces a refetch when the query didn't change, e.g. after a create with an empty search box.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    // Debounced responses can land out of order; the flag makes superseded ones a no-op.
    let cancelled = false;

    async function fetchClients() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/clients", {
        params: {
          query: {
            Search: activeSearch,
            Limit: PAGE_LIMIT,
            Type: typeFilter === "all" ? undefined : typeFilter,
            ShowClones: showClones,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load clients.");
      } else {
        setClients(data?.data ?? []);
      }
      setLoading(false);
      setHasLoaded(true);
    }

    fetchClients();
    return () => {
      cancelled = true;
    };
  }, [activeSearch, typeFilter, showClones, reloadToken]);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => createdTime(b) - createdTime(a)),
    [clients],
  );

  // Clear the search so the new client shows; the reload token covers an already-empty box.
  const handleCreated = useCallback(() => {
    setSearchInput("");
    setReloadToken((t) => t + 1);
  }, []);

  const handleUpdated = useCallback((updated: Client) => {
    // PUT /clients/{id} returns ClientResponse, without sourceClientId/clonedAt: merge so clone provenance survives.
    setClients((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
  }, []);

  // Only the first load blanks the page: swapping the search box for a spinner would lose focus mid-word.
  if (!hasLoaded && loading) {
    return (
      <div className="mx-auto w-full max-w-[100rem] flex items-center justify-center p-16">
        <Spinner />
      </div>
    );
  }

  const searchPending = searchInput.trim().length > 0 && searchInput.trim().length < MIN_SEARCH_LENGTH;

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Clients</h1>
          {loading && <Spinner className="size-5" />}
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <PlusIcon className="size-4" />
          Create client
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            type="search"
            placeholder="Search clients by name..."
            aria-label="Search clients by name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as TypeFilter)}>
          <SelectTrigger className="w-36" aria-label="Filter by client type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPE_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch id="show-clones" checked={showClones} onCheckedChange={setShowClones} />
          <Label htmlFor="show-clones" className="text-sm font-normal">
            Show clones
          </Label>
        </div>
        {searchPending && (
          <p className="text-muted-foreground text-sm">
            Type {MIN_SEARCH_LENGTH} characters to search
          </p>
        )}
      </div>

      {error ? (
        <p className="text-destructive">{error}</p>
      ) : sortedClients.length === 0 ? (
        // Different facts, different actions: retype the search vs create a client.
        <p className="text-muted-foreground">
          {activeSearch
            ? `No clients match “${activeSearch}”.`
            : typeFilter !== "all" || !showClones
              ? "No clients match the current filters."
              : "No clients found."}
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>ID</TableHead>
                <TableHead className="text-right">Dolfin rev share</TableHead>
                <TableHead className="text-right">Client rev share</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedClients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell className="font-medium">
                    <Link to={`/clients/${client.id}`} className="hover:underline">
                      {client.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {client.type ? (
                        <Badge variant={client.type === "Live" ? "default" : "outline"}>
                          {client.type}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {client.sourceClientId && (
                        <Badge variant="outline" className="text-[10px]">
                          Clone
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Link to={`/clients/${client.id}`}>
                      <Badge
                        variant="secondary"
                        className="font-mono text-xs hover:bg-secondary/80"
                      >
                        {client.id}
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercent(client.dolfinRevenueSharePercent)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercent(client.paymentRevenueSharePercent)}
                  </TableCell>
                  <TableCell>{formatDateTime(client.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {client.id && <ClientStatsHoverCard clientId={client.id} />}
                      <Button asChild variant="ghost" size="sm" className="gap-1">
                        <Link to={`/clients/${client.id}#organisations`}>
                          <BuildingIcon className="size-4" />
                          View orgs
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Edit client"
                        onClick={() => setEditClient(client)}
                      >
                        <PencilIcon className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateClientDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />

      {editClient && (
        <EditClientDialog
          client={editClient}
          open={editClient !== null}
          onOpenChange={(open) => {
            if (!open) setEditClient(null);
          }}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
}
